import React from 'react';
import { Table, TableProps } from 'antd';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './DraggableTable.scss';

let dragingIndex = -1;

interface BodyRowProps {
  isOver: boolean;
  style?: React.CSSProperties;
  index: number;
  className?: string;
  children: React.ReactNode;
  moveRow: (dragIndex: number, hoverIndex: number) => void; // Add moveRow to BodyRowProps
}

const BodyRow: React.FC<BodyRowProps> = (props) => {
  const style = { ...props.style, cursor: 'move' };

  let { className } = props;
  if (props.isOver) {
    if (props.index > dragingIndex) {
      className += ' drop-over-downward';
    }
    if (props.index < dragingIndex) {
      className += ' drop-over-upward';
    }
  }

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ROW',
    item: { index: props.index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const [{ isOverCurrent }, drop] = useDrop(() => ({
    accept: 'ROW',
    drop: (item: { index: number }) => {
      const dragIndex = item.index;
      const hoverIndex = props.index;

      if (dragIndex === hoverIndex) {
        return;
      }

      props.moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOverCurrent: monitor.isOver(),
    }),
  }));

  return (
    <tr
      ref={(node) => {
        if (node) {
          drag(drop(node))
        }
      }}
      className={className}
      style={{ ...style, opacity: isDragging ? 0.5 : 1 }}
    >
      {props.children}
    </tr>
  );
};

interface DropProps {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
}

interface DragSortingTableProps<T> extends TableProps<T> {
  onDragSort?: (sortedData: T[]) => void;
  draggedRow: (index: number, id: string) => void | Promise<any>;
}

type extractTableType<T> = T extends DragSortingTableProps<any> ? T : never;

export const DragSortingTable: <T>(
  props: DragSortingTableProps<T>
) => React.ReactElement<DragSortingTableProps<T>> = (props) => {
  const [dataSource, setDataSource] = React.useState<any>(props.dataSource);

  React.useEffect(() => {
    setDataSource(props.dataSource);
  }, [props.dataSource]);

  const components: any = {
    body: {
      row: (rowProps: any) => (
        <BodyRow {...rowProps} moveRow={moveRow} />
      ),
    },
  };

  const moveRow: DropProps['moveRow'] = (dragIndex, hoverIndex) => {
    let meanIndex = -1;
    const dragRow = dataSource[dragIndex];
    const remaining = dataSource.filter((i: any) => i !== dragRow);
    const sorted = [
      ...remaining.slice(0, hoverIndex),
      dragRow,
      ...remaining.slice(hoverIndex),
    ];

    setDataSource(sorted);

    if (sorted[hoverIndex - 1]?.position && sorted[hoverIndex + 1]?.position) {
      meanIndex =
        (sorted[hoverIndex - 1].position + sorted[hoverIndex + 1].position) / 2;
    } else if (sorted[hoverIndex + 1]?.position) {
      meanIndex = sorted[hoverIndex + 1]?.position - 1;
    } else if (sorted[hoverIndex - 1]?.position) {
      meanIndex = sorted[hoverIndex - 1].position + 1;
    }

    if (props.onDragSort) {
      props.onDragSort(sorted);
    }
    if (props.draggedRow) {
      props.draggedRow(meanIndex, dragRow.id);
    }
  };

  const tableProps: TableProps<any> = {
    ...props,
    className: props.className
      ? props.className + ' drag-sorting-table'
      : 'drag-sorting-table',
    components,
    dataSource,
    onRow: (_record, index) => ({ index, moveRow, className: 'draggable-row' }),
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Table {...tableProps}>{props.children}</Table>
    </DndProvider>
  );
};